import React, { useState, useMemo } from 'react';
import { ChevronLeft, ChevronRight, Loader2 } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useStacks } from '@/lib/hooks';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import {
    Sheet,
    SheetContent,
    SheetHeader,
    SheetTitle,
    SheetTrigger,
} from '@/components/ui/sheet';

export function HistoryPage() {
    const { getHistoryForMonth, stacks, isLoading } = useStacks();

    const [currentDate, setCurrentDate] = useState(new Date());

    const year = currentDate.getFullYear();
    const month = currentDate.getMonth();

    const monthName = currentDate.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });

    const monthHistory = useMemo(() =>
        getHistoryForMonth(year, month),
        [getHistoryForMonth, year, month]
    );

    const calendarDays = useMemo(() => {
        const firstDay = new Date(year, month, 1);
        const lastDay = new Date(year, month + 1, 0);
        const startPadding = firstDay.getDay();
        const totalDays = lastDay.getDate();

        const days = [];

        for (let i = 0; i < startPadding; i++) {
            days.push({ day: null, date: null });
        }

        for (let day = 1; day <= totalDays; day++) {
            const date = new Date(year, month, day);
            const dateStr = date.toISOString().split('T')[0];
            const data = monthHistory[dateStr] || { totalItems: 0, stacksRan: 0 };
            const isToday = dateStr === new Date().toISOString().split('T')[0];
            const isFuture = date > new Date();

            days.push({
                day,
                date: dateStr,
                ...data,
                isToday,
                isFuture,
            });
        }

        return days;
    }, [year, month, monthHistory]);

    const prevMonth = () => {
        setCurrentDate(new Date(year, month - 1, 1));
    };

    const nextMonth = () => {
        const nextDate = new Date(year, month + 1, 1);
        if (nextDate <= new Date()) {
            setCurrentDate(nextDate);
        }
    };

    const canGoNext = new Date(year, month + 1, 1) <= new Date();

    const getIntensityClass = (totalItems) => {
        if (totalItems === 0) return 'bg-transparent border border-border';
        if (totalItems <= 3) return 'bg-primary/30';
        if (totalItems <= 6) return 'bg-primary/50';
        if (totalItems <= 9) return 'bg-primary/70';
        return 'bg-primary';
    };

    if (isLoading) {
        return (
            <div className="max-w-lg mx-auto px-4 py-6 flex items-center justify-center min-h-[50vh]">
                <Loader2 className="w-8 h-8 animate-spin text-primary" />
            </div>
        );
    }

    return (
        <div className="max-w-lg mx-auto px-4 py-6">
            <header className="flex items-center justify-between mb-6">
                <h1 className="text-2xl font-bold text-foreground">History</h1>
            </header>

            {/* Month navigation */}
            <div className="flex items-center justify-between mb-4">
                <Button variant="ghost" size="icon" onClick={prevMonth}>
                    <ChevronLeft className="w-5 h-5" />
                </Button>
                <h2 className="font-semibold">{monthName}</h2>
                <Button
                    variant="ghost"
                    size="icon"
                    onClick={nextMonth}
                    disabled={!canGoNext}
                >
                    <ChevronRight className="w-5 h-5" />
                </Button>
            </div>

            {/* Calendar */}
            <Card className="mb-4">
                <CardContent className="p-3">
                    {/* Day headers */}
                    <div className="grid grid-cols-7 gap-1 mb-1">
                        {['S', 'M', 'T', 'W', 'T', 'F', 'S'].map((day, i) => (
                            <div key={i} className="text-center text-[10px] text-muted-foreground font-medium py-1">
                                {day}
                            </div>
                        ))}
                    </div>

                    {/* Calendar grid */}
                    <div className="grid grid-cols-7 gap-1">
                        {calendarDays.map((dayData, index) => (
                            <Sheet key={index}>
                                <SheetTrigger asChild>
                                    <button
                                        disabled={!dayData.day || dayData.isFuture}
                                        className={cn(
                                            "aspect-square rounded flex flex-col items-center justify-center p-0.5 transition-all",
                                            dayData.day && !dayData.isFuture && "hover:ring-1 hover:ring-primary/30",
                                            dayData.isToday && "ring-1 ring-primary",
                                            !dayData.day && "invisible"
                                        )}
                                    >
                                        {dayData.day && (
                                            <>
                                                <div className={cn(
                                                    "w-5 h-5 rounded mb-0.5",
                                                    dayData.isFuture
                                                        ? "bg-muted/50"
                                                        : getIntensityClass(dayData.totalItems)
                                                )} />
                                                <span className={cn(
                                                    "text-[9px]",
                                                    dayData.isToday
                                                        ? "text-primary font-semibold"
                                                        : "text-muted-foreground"
                                                )}>
                                                    {dayData.day}
                                                </span>
                                            </>
                                        )}
                                    </button>
                                </SheetTrigger>

                                {dayData.day && !dayData.isFuture && (
                                    <SheetContent side="bottom" className="rounded-t-2xl">
                                        <SheetHeader>
                                            <SheetTitle>
                                                {new Date(dayData.date).toLocaleDateString('en-US', {
                                                    weekday: 'long',
                                                    month: 'long',
                                                    day: 'numeric'
                                                })}
                                            </SheetTitle>
                                        </SheetHeader>

                                        <div className="py-4">
                                            {dayData.stacksRan > 0 ? (
                                                <>
                                                    <p className="text-muted-foreground mb-4 text-sm">
                                                        {dayData.stacksRan} {dayData.stacksRan === 1 ? 'stack' : 'stacks'} ran • {dayData.totalItems} items
                                                    </p>

                                                    <div className="space-y-2">
                                                        {stacks
                                                            .filter(s => !s.archived_at)
                                                            .map(stack => {
                                                                const stackCompletions = dayData.completions?.filter(c => c.stack_id === stack.id) || [];
                                                                if (stackCompletions.length === 0) return null;

                                                                return (
                                                                    <div key={stack.id} className="flex items-center justify-between p-3 bg-muted rounded-lg">
                                                                        <span className="font-medium text-sm">{stack.name}</span>
                                                                        <span className="text-xs text-muted-foreground">
                                                                            {stackCompletions.length}/{stack.items.length}
                                                                        </span>
                                                                    </div>
                                                                );
                                                            })}
                                                    </div>
                                                </>
                                            ) : (
                                                <p className="text-muted-foreground text-center py-6 text-sm">
                                                    Nothing completed this day.
                                                </p>
                                            )}
                                        </div>
                                    </SheetContent>
                                )}
                            </Sheet>
                        ))}
                    </div>
                </CardContent>
            </Card>

            {/* Legend */}
            <div className="flex items-center justify-center gap-3 text-[10px] text-muted-foreground">
                <span>Less</span>
                <div className="flex gap-0.5">
                    <div className="w-3 h-3 rounded border border-border" />
                    <div className="w-3 h-3 rounded bg-primary/30" />
                    <div className="w-3 h-3 rounded bg-primary/50" />
                    <div className="w-3 h-3 rounded bg-primary/70" />
                    <div className="w-3 h-3 rounded bg-primary" />
                </div>
                <span>More</span>
            </div>
        </div>
    );
}
